import { css } from '@emotion/react';
import styled from '@emotion/styled';
import * as React from 'react';
import { useEffect, useRef } from 'react';
import { useDeepLinking } from '../hooks/useDeepLinking';
import { useLightboxDisplay } from '../hooks/useLightboxDisplay';
import { PHONE_MEDIA } from '../vars';
import { inlineCodePill } from './inlineCodePill';
import { SCROLL } from './scroll';
import { headingTypography } from './typography';

export interface ContentWidgetProps {
  className?: any;
  variant?: 'default' | 'article';
}

export const ContentWidget: React.FC<React.PropsWithChildren<ContentWidgetProps>> = (props) => {
  let ref = useRef<HTMLDivElement>(null);

  useDeepLinking({ forwardRef: ref });
  useLightboxDisplay({ forwardRef: ref });

  useEffect(() => {
    if (!ref.current) {
      return;
    }

    const tables = ref.current.querySelectorAll('table');
    tables.forEach((table) => {
      const tableHead = table.querySelector('thead');
      if (!tableHead) {
        return;
      }

      const headerCells = Array.from(tableHead.querySelectorAll('th, td'));
      const hasVisibleHeaderContent = headerCells.some((cell) => (cell.textContent ?? '').trim().length > 0);

      if (!hasVisibleHeaderContent) {
        tableHead.remove();
      }
    });

    const videos = ref.current.querySelectorAll('video');
    videos.forEach((video) => {
      video.autoplay = true;
      video.muted = true;
      video.loop = true;
      video.playsInline = true;

      void video.play().catch(() => {
        // Ignore blocked autoplay attempts and leave controls available.
      });
    });
  }, [props.children]);

  return (
    <S.Container ref={ref} className={props.className} $variant={props.variant ?? 'default'}>
      {props.children}
    </S.Container>
  );
};
namespace S {
  const getScale = (variant: ContentWidgetProps['variant']) => {
    if (variant === 'article') {
      return {
        contentGap: 24,
        h1: 34,
        h2: 24,
        h3: 20,
        paragraph: 14,
        list: 13,
        table: 13,
        code: 12,
        codePadding: '16px 18px'
      };
    }

    return {
      contentGap: 30,
      h1: 50,
      h2: 40,
      h3: 30,
      paragraph: 15,
      list: 14,
      table: 14,
      code: 13,
      codePadding: '20px 22px'
    };
  };

  const headingAnchor = css`
    cursor: pointer;
    position: relative;

    :before {
      content: '#';
      opacity: 0;
      // Position abs here ensures the symbol doesn't cause the heading to indent
      position: absolute;
      // Makes sure the symbol is stuck to the right edge of the heading
      transform: translateX(-100%);
      box-sizing: content-box;
      text-align: right;
    }

    &:hover {
      :before {
        opacity: 0.5;
      }
    }
  `;

  const panelFrame = css`
    overflow: hidden;
    border-radius: var(--content-list-radius);
  `;

  const centeredMedia = css`
    max-width: 100%;
    margin-left: auto;
    margin-right: auto;
    display: block;
  `;

  export const Container = styled.div<{ $variant: ContentWidgetProps['variant'] }>`
    --content-list-radius: ${(p) => p.theme.text.listRadius};

    /* Flow */
    > h1,
    > h2,
    > h3,
    > h4 {
      margin-top: ${(p) => Math.round(getScale(p.$variant).contentGap * 1.7)}px;

      &:first-child {
        margin-top: 0;
      }
    }

    > img,
    > video,
    > table,
    > div[data-content-table],
    > a,
    > iframe,
    > blockquote,
    > aside[data-content-callout],
    > aside[data-content-version-compatibility],
    > div[data-content-attribute-reference],
    > aside[data-content-language-tabs],
    > aside[data-content-syntax-card],
    > hr,
    > ul,
    > ol,
    > pre,
    > p {
      margin-top: ${(p) => Math.round(getScale(p.$variant).contentGap * 0.75)}px;

      &:first-child {
        margin-top: 0;
      }
    }

    hr {
      opacity: 0.2;
    }

    /* Typography */
    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      ${headingAnchor};
    }

    h1 {
      font-size: ${(p) => getScale(p.$variant).h1}px;
      line-height: 1.1;

      :before {
        content: none;
      }
    }

    h2 {
      font-size: ${(p) => getScale(p.$variant).h2}px;
      line-height: 1.2;
    }

    h3 {
      font-size: ${(p) => getScale(p.$variant).h3}px;
      line-height: 1.25;
    }

    h1,
    h2,
    h3,
    h4,
    h5 {
      ${(p) => headingTypography(p.theme)};
    }

    h4,
    h5 {
      color: ${(p) => p.theme.text.paragraph};
    }

    p {
      color: ${(p) => p.theme.text.paragraph};
      font-size: ${(p) => getScale(p.$variant).paragraph}px;
      line-height: 1.75;
      font-weight: 400;
    }

    p,
    li {
      strong,
      b {
        color: ${(p) => p.theme.text.heading};
        font-weight: 600;
      }
    }

    a {
      text-decoration: none;
      color: #8f59ff;

      &:hover {
        text-decoration: underline;
      }
    }

    /* Surfaces */
    [data-content-table] {
      max-width: 100%;
      overflow-x: auto;
      border-radius: var(--content-list-radius);
      box-shadow: ${(p) => p.theme.text.codeBlockShadow};

      ${(p) => SCROLL(p.theme, true)};

      > table {
        margin: 0;
        box-shadow: none;
      }
    }

    table {
      ${panelFrame};
      width: 100%;
      border-spacing: 0;
      border-collapse: separate;
      border: 1px solid ${(p) => p.theme.text.tableBorder};
      background: ${(p) => p.theme.text.tableBackground};
      box-shadow: ${(p) => p.theme.text.codeBlockShadow};

      thead {
        th {
          background: ${(p) => p.theme.text.tableHeaderBackground};
          color: ${(p) => p.theme.text.tableHeaderText};
          opacity: 1;
          font-weight: 700;
          font-size: 13px;
          letter-spacing: 0.02em;
          border-bottom: 1px solid ${(p) => p.theme.text.tableBorder};
        }
      }

      td,
      th {
        padding: 12px 14px;
        text-align: left;
        vertical-align: top;
      }

      tbody {
        tr:nth-of-type(odd) {
          background: ${(p) => p.theme.text.tableRowOdd};
        }

        tr:nth-of-type(even) {
          background: ${(p) => p.theme.text.tableRowEven};
        }

        td {
          color: ${(p) => p.theme.text.paragraph};
          font-size: ${(p) => getScale(p.$variant).table}px;
          line-height: 1.65;
          border-top: 1px solid ${(p) => p.theme.text.tableBorder};
        }

        tr:first-of-type td {
          border-top: 0;
        }

        td strong,
        td b {
          color: ${(p) => p.theme.text.heading};
          font-weight: 600 !important;
        }

        tr:hover td {
          background: rgba(255, 255, 255, 0.06);
        }
      }
    }

    blockquote {
      padding: 22px 28px 22px 26px;
      position: relative;
      overflow: hidden;
      border-radius: calc(var(--content-list-radius) + 4px);
      border: 1px solid ${(p) => p.theme.text.listBorder};
      border-left: 4px solid ${(p) => p.theme.text.special};
      background: ${(p) => p.theme.text.blockquoteBackground};
      box-shadow: ${(p) => p.theme.text.blockquoteShadow};

      &::before {
        content: '“';
        position: absolute;
        top: 10px;
        right: 16px;
        font-family: 'Inter Tight Variable', sans-serif;
        font-size: 72px;
        line-height: 1;
        color: ${(p) => p.theme.text.blockquoteQuoteColor};
        pointer-events: none;
      }

      p {
        position: relative;
        z-index: 1;
        margin-top: 0;
        max-width: calc(100% - 28px);
        color: ${(p) => p.theme.text.heading};
        font-size: ${(p) => getScale(p.$variant).paragraph}px;
        line-height: 1.75;
        text-align: left;

        & + p {
          margin-top: 12px;
        }
      }
    }

    > ul,
    > ol {
      padding: 14px;
      border: 1px solid ${(p) => p.theme.text.listBorder};
      border-radius: var(--content-list-radius);
      background: ${(p) => p.theme.text.listBackground};
    }

    ul,
    ol {
      padding-left: 30px;
    }

    li {
      margin-bottom: 10px;
      font-size: ${(p) => getScale(p.$variant).list}px;
      line-height: 1.65;
      color: ${(p) => p.theme.text.paragraph};
    }

    li:last-child {
      margin-bottom: 0;
    }

    li > ul,
    li > ol {
      margin: 10px 0;
      padding-left: 1.75rem;
    }

    li > a:only-child {
      line-height: inherit;

      > *:first-child {
        margin-top: 0;
      }

      > *:last-child {
        margin-bottom: 0;
      }

      p {
        display: inline;
        margin: 0;
        line-height: inherit;
      }
    }

    // Inline syntax uses the same treatment in prose and tables.
    p,
    li,
    th,
    td {
      code {
        ${(p) => inlineCodePill(p.theme)};
      }
    }

    pre {
      ${panelFrame};
      overflow-x: auto;
      overflow-y: hidden;
      background: ${(p) => p.theme.text.codeBlockBackground};
      border: 1px solid ${(p) => p.theme.text.codeBlockBorder};
      box-shadow: ${(p) => p.theme.text.codeBlockShadow};

      ${(p) => SCROLL(p.theme, true)};

      code {
        display: block;
        min-width: max-content;
        background: transparent;
        border-radius: 0;
        border: 0 transparent;
        box-shadow: none;
        padding: ${(p) => getScale(p.$variant).codePadding};
        font-size: ${(p) => getScale(p.$variant).code}px;
        line-height: 1.65;

        &.hljs {
          background: transparent;
        }
      }
    }

    /* Media */
    img,
    video {
      ${panelFrame};
      ${centeredMedia};
      box-shadow: ${(p) => p.theme.text.mediaShadow};
      max-height: 600px;
    }

    img {
      transition: transform 0.5s;
      cursor: pointer;
      width: auto;
      height: auto;

      &:hover {
        transform: scale(1.02);
      }
    }

    img[src$='.svg'] {
      box-shadow: ${(p) => (p.theme.light ? p.theme.text.mediaShadow : 'none')};
    }

    img.original-size,
    img[data-size='original-size'] {
      max-height: none;
    }

    img.compact {
      max-width: min(280px, 100%);
    }

    img.small {
      max-width: min(400px, 100%);
    }

    img.medium {
      max-width: min(560px, 100%);
    }

    img.large {
      max-width: min(760px, 100%);
    }

    /* Responsive */
    @media (max-width: ${PHONE_MEDIA}px) {
      h1,
      h2,
      h3,
      h4,
      h5,
      h6 {
        text-align: center;
      }

      p {
        text-align: justify;
      }

      blockquote {
        padding: 20px 22px 20px 20px;

        &::before {
          top: 12px;
          right: 12px;
          font-size: 56px;
        }

        p {
          max-width: 100%;
          text-align: left;
        }
      }
    }
  `;
}
